import { RoomReadbleTO } from "@/models/detail";
import stylesCommon from "@/styles/detail/DetailBookingSidebar/DetailBookingConfigurator.module.scss";
import styles from "@/styles/detail/DetailBookingSidebar/RoomTravellersSection.module.scss";
import React, {
    useCallback,
    // useCallback,
    // Dispatch,
    // SetStateAction,
    // useCallback,
    useEffect,
    useMemo,
    useRef,
    // useState,
} from "react";
import { ENUM_ECCESSO, ERoomType } from "@/models/common/enums";
import { useStore } from "@/store/index";
import { IRoom } from "@/models/checkout";
import { MDBCol, MDBRow, MDBSwitch } from "mdb-react-ui-kit";
import userInterfaceSvg from "@/utils/svg-icon/userInterface";
import Input from "@/components/common/Input";
import { textCapitalize } from "@/utils/common/textCapitalize";
import RowTravellerSectionDetail from "./RowTravellerSectionDetail";

const RoomTravellersSectionDetail = ({
    rooms,
    calculateSelectedSlot,
    errorGuest,
    setErrorGuest,
    pets,
    setPets,
    eccessoList,
    setEccessoList,
}: {
    rooms: IRoom[];
    calculateSelectedSlot: (item: IRoom) => RoomReadbleTO | undefined;
    errorGuest: number;
    setErrorGuest: React.Dispatch<React.SetStateAction<number>>;
    pets: boolean;
    setPets?: React.Dispatch<React.SetStateAction<boolean>>;
    eccessoList: ENUM_ECCESSO[];
    setEccessoList: React.Dispatch<React.SetStateAction<ENUM_ECCESSO[]>>;
}) => {
    const dispatch = useStore(state => state?.dispatchCheckout);

    const originalAmount = useStore(
        state => state.checkoutState.orderData.originalAmount
    );

    const { CloseLine } = userInterfaceSvg;

    const mapRooms = (roomsSelection: IRoom[]): IRoom[] =>
        roomsSelection.map(room => ({
            type: room.type,
            adults: room.adults,
            children: room.children,
            newborns: room.newborns,
        }));

    const calculateChildrens = useMemo(() => {
        const eccessoChildrens = eccessoList.filter(
            item => item === ENUM_ECCESSO.CHILDREN
        ).length;

        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return eccessoChildrens;
        }

        const children = rooms.reduce((previousValue, currentValue) => {
            return previousValue + (currentValue.children?.length ?? 0);
        }, 0);

        return children + eccessoChildrens;
    }, [rooms, eccessoList]);

    const calculatenewborns = useMemo(() => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return 0;
        }
        return rooms.reduce((previousValue, currentValue) => {
            return previousValue + (currentValue.newborns ?? 0);
        }, 0);
    }, [rooms]);

    const handleUpdateRoom = useCallback(
        (updatedIndex: number, room: IRoom) => {
            if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
                return;
            }
            dispatch({
                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                // payload: rooms,
                payload: mapRooms(
                    rooms.map((value, index) => {
                        return updatedIndex === index ? room : value;
                    })
                ),
            });
        },
        [rooms, dispatch]
    );

    const calculateAdults = useMemo(() => {
        const eccessoAdults = eccessoList.filter(
            item => item === ENUM_ECCESSO.ADULT
        ).length;

        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return eccessoAdults;
        }

        const adultsRoom = rooms.reduce((previousValue, currentValue) => {
            return previousValue + currentValue.adults;
        }, 0);

        return eccessoAdults + adultsRoom;
    }, [eccessoList, rooms]);

    const distributeAdults = (roomsParams: IRoom[]) => {
        const remainingAdults = roomsParams.reduce(
            (sum, room) => sum + room.adults,
            0
        );

        // Reset adults in all rooms and distribute adults evenly
        return roomsParams.map((room, index) => ({
            ...room,
            adults: index < remainingAdults ? 1 : 0,
        }));
    };

    // Usa useRef per tracciare se abbiamo già distribuito gli adults
    const distributedRef = useRef<string>("");
    const lastRoomsKeyRef = useRef<string>("");

    useEffect(() => {
        if (!rooms || !Array.isArray(rooms)) {
            return;
        }

        // Crea una chiave univoca basata sui valori attuali delle rooms
        const currentRoomsKey = JSON.stringify(
            rooms.map(r => ({
                type: r.type,
                adults: r.adults,
                children: r.children?.length ?? 0,
                newborns: r.newborns ?? 0,
            }))
        );

        // Se le rooms non sono cambiate, non fare nulla
        if (lastRoomsKeyRef.current === currentRoomsKey) {
            return;
        }
        lastRoomsKeyRef.current = currentRoomsKey;

        if (rooms.length === 2 && calculateAdults === 2) {
            const newRooms = distributeAdults(rooms);

            // Crea una chiave univoca basata sui valori delle newRooms
            const newRoomsKey = JSON.stringify(
                newRooms.map(r => ({
                    type: r.type,
                    adults: r.adults,
                    children: r.children?.length ?? 0,
                    newborns: r.newborns ?? 0,
                }))
            );

            // Se abbiamo già distribuito con questa configurazione, non rifare
            if (distributedRef.current === newRoomsKey) {
                return;
            }

            // Confronta se newRooms è diverso da rooms prima di fare il dispatch
            const roomsChanged = newRooms.some((newRoom, index) => {
                const currentRoom = rooms[index];
                return (
                    !currentRoom ||
                    newRoom.type !== currentRoom.type ||
                    newRoom.adults !== currentRoom.adults ||
                    newRoom.children?.length !== currentRoom.children?.length ||
                    newRoom.newborns !== currentRoom.newborns
                );
            });

            if (roomsChanged) {
                dispatch({
                    type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                    payload: newRooms,
                });
                // Segna come distribuito
                distributedRef.current = newRoomsKey;
            }
        } else {
            // Reset il ref se le condizioni non sono più valide
            distributedRef.current = "";
        }
    }, [calculateAdults, rooms, dispatch]);

    useEffect(() => {
        setErrorGuest(eccessoList.length);
    }, [eccessoList.length, setErrorGuest]);

    const addToddlerProcess = (index: number, type: ERoomType) => {
        handleUpdateRoom(index, {
            type: (rooms.at(index)?.type as ERoomType) ?? type,

            adults: rooms.at(index)?.adults ?? 0,
            children: rooms.at(index)?.children ?? [],
            newborns: (rooms.at(index)?.newborns ?? 0) + 1,
        });
    };

    const addToddler = () => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return;
        }
        let flag = false;
        for (let index = 0; index < rooms.length; index++) {
            if ((rooms.at(index)?.newborns ?? 0) < 3) {
                flag = true;
                addToddlerProcess(index, ERoomType.DOUBLE);
                break;
            }
        }
        if (flag === false) {
            const roomTriple = rooms.findIndex(
                item => item.type === ERoomType.TRIPLE
            );
            if (roomTriple !== -1) {
                addToddlerProcess(roomTriple, ERoomType.TRIPLE);
            } else if (roomTriple === -1) {
                const roomDouble = rooms.findIndex(
                    item => item.type === ERoomType.DOUBLE
                );
                if (roomDouble !== -1) {
                    addToddlerProcess(roomDouble, ERoomType.DOUBLE);
                } else {
                    const roomSingle = rooms.findIndex(
                        item => item.type === ERoomType.SINGLE
                    );
                    addToddlerProcess(roomSingle, ERoomType.SINGLE);
                }
            }
        }
    };

    const deletenewborns = () => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return;
        }
        // eslint-disable-next-line for-direction
        for (let index = rooms.length; index <= rooms.length; index--) {
            if ((rooms.at(index)?.newborns ?? 0) > 0) {
                handleUpdateRoom(index, {
                    type: rooms.at(index)?.type as ERoomType,
                    adults: rooms.at(index)?.adults ?? 0,
                    newborns: (rooms.at(index)?.newborns ?? 1) - 1,
                    children: rooms.at(index)?.children ?? [],
                });
                break;
            }
        }
    };

    // useEffect(() => {
    //     if (router.asPath.includes("detail")) {
    //         const url = new URL(window.location.href);
    //         url.searchParams.set("rooms", JSON.stringify(rooms));
    //         url.searchParams.set("eccessoList", JSON.stringify(eccessoList));
    //         window.history.replaceState(null, "", url);
    //     }
    // }, [rooms, router.asPath, eccessoList]);

    const roomCapacities = useMemo(
        () => ({
            [ERoomType.SINGLE]: 1,
            [ERoomType.DOUBLE]: 2,
            [ERoomType.TRIPLE]: 3,
        }),
        []
    );

    const roomTypeArrayOptions = [
        {
            value: ERoomType.SINGLE,
            label: "Single",
        },
        {
            value: ERoomType.DOUBLE,
            label: "Double",
        },
        {
            value: ERoomType.TRIPLE,
            label: "Triple",
        },
    ];

    const addNewAdult = useCallback(() => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return;
        }
        const index = rooms.findIndex(item => {
            const selectedSlot = calculateSelectedSlot(item);
            const capacity =
                selectedSlot?.capacity ?? roomCapacities[item.type];
            return item.adults + item.children.length + 1 <= capacity;
        });

        // const finalIndex = index === -1 ? 0 : index;

        // const index = rooms.findIndex(
        //     item =>
        //         item.adults + item.children.length + 1 <=
        //         (calculateSelectedSlot(item)?.capacity ?? 0)
        // );

        if (index > -1) {
            const indexEccesso = eccessoList.findLastIndex(
                item => item === ENUM_ECCESSO.ADULT
            );

            if (eccessoList.length > 0 && indexEccesso > -1) {
                const left = eccessoList.slice(0, indexEccesso);
                const right = eccessoList.slice(indexEccesso + 1);
                eccessoList.splice(indexEccesso, 1);

                setEccessoList([...left, ...right]);
            }

            handleUpdateRoom(index, {
                type: (rooms.at(index)?.type as ERoomType) ?? ERoomType.DOUBLE,
                adults: (rooms.at(index)?.adults ?? 0) + 1,
                children: rooms.at(index)?.children ?? [],
                newborns: rooms.at(index)?.newborns ?? 0,
            });
        } else {
            setEccessoList(eccessoList.concat(ENUM_ECCESSO.ADULT));
        }
    }, [
        eccessoList,
        rooms,
        calculateSelectedSlot,
        roomCapacities,
        handleUpdateRoom,
        setEccessoList,
    ]);

    const addNewChildren = useCallback(() => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return;
        }
        // const index = rooms.findIndex(item => {
        //     const selectedSlot = calculateSelectedSlot(item);
        //     if (!selectedSlot) return false;
        //     const { capacity } = selectedSlot;
        //     const currentChildren = item.children.length;
        //     return (
        //         item.adults + currentChildren + 1 <= capacity &&
        //         currentChildren < 2
        //     );
        // });
        const index = rooms.findIndex(item => {
            const selectedSlot = calculateSelectedSlot(item);
            const currentChildren = item.children.length;

            const capacity =
                selectedSlot?.capacity ?? roomCapacities[item.type];
            return (
                item.adults + currentChildren + 1 <= capacity &&
                currentChildren < 2
            );
        });
        if (index > -1) {
            const indexEccesso = eccessoList.findLastIndex(
                item => item === ENUM_ECCESSO.CHILDREN
            );

            if (eccessoList.length > 0 && indexEccesso > -1) {
                const left = eccessoList.slice(0, indexEccesso);
                const right = eccessoList.slice(indexEccesso + 1);
                setEccessoList([...left, ...right]);
            }
            handleUpdateRoom(index, {
                type: rooms.at(index)?.type as ERoomType,
                adults: rooms.at(index)?.adults ?? 0,
                children: rooms.at(index)?.children.concat(1) ?? [],
                newborns: rooms.at(index)?.newborns ?? 0,
            });
        } else {
            setEccessoList(eccessoList.concat(ENUM_ECCESSO.CHILDREN));
        }
    }, [
        eccessoList,
        rooms,
        calculateSelectedSlot,
        handleUpdateRoom,
        roomCapacities,
        setEccessoList,
    ]);

    const removeAdult = useCallback(() => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return;
        }
        const indexEccesso = eccessoList.findLastIndex(
            item => item === ENUM_ECCESSO.ADULT
        );

        if (eccessoList.length > 0 && indexEccesso > -1) {
            const left = eccessoList.slice(0, indexEccesso);
            const right = eccessoList.slice(indexEccesso + 1);
            eccessoList.splice(indexEccesso, 1);

            setEccessoList([...left, ...right]);
        } else {
            const indexRoom = rooms.findIndex(item => {
                const selectedSlot = calculateSelectedSlot(item);
                const capacity =
                    selectedSlot?.capacity ?? roomCapacities[item.type];
                return item.adults - 1 <= capacity && item.adults - 1 > 0;
            });

            handleUpdateRoom(indexRoom, {
                type:
                    (rooms.at(indexRoom)?.type as ERoomType) ??
                    ERoomType.DOUBLE,
                adults: (rooms.at(indexRoom)?.adults ?? 2) - 1,
                children: rooms.at(indexRoom)?.children ?? [],
                newborns: rooms.at(indexRoom)?.newborns ?? 0,
            });
        }
    }, [
        eccessoList,
        rooms,
        calculateSelectedSlot,
        roomCapacities,
        handleUpdateRoom,
        setEccessoList,
    ]);

    const removeChildren = useCallback(() => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return;
        }
        const indexEccesso = eccessoList.findLastIndex(
            item => item === ENUM_ECCESSO.CHILDREN
        );

        if (eccessoList.length > 0 && indexEccesso > -1) {
            const left = eccessoList.slice(0, indexEccesso);
            const right = eccessoList.slice(indexEccesso + 1);
            eccessoList.splice(indexEccesso, 1);

            setEccessoList([...left, ...right]);
        } else {
            // const indexRoom = rooms.findLastIndex(
            //     item =>
            //         item.children.length - 1 <=
            //             (calculateSelectedSlot(item)?.capacity ?? 0) &&
            //         item.children.length - 1 >= 0
            // );
            const indexRoom = rooms.findIndex(item => {
                const selectedSlot = calculateSelectedSlot(item);
                const capacity =
                    selectedSlot?.capacity ?? roomCapacities[item.type];
                return (
                    item.children.length - 1 <= capacity &&
                    item.children.length - 1 >= 0
                );
            });

            handleUpdateRoom(indexRoom, {
                type:
                    (rooms.at(indexRoom)?.type as ERoomType) ??
                    ERoomType.DOUBLE,
                adults: rooms.at(indexRoom)?.adults ?? 1,
                children: rooms.at(indexRoom)?.children.slice(0, -1) ?? [],
                newborns: rooms.at(indexRoom)?.newborns ?? 0,
            });
        }
    }, [
        eccessoList,
        rooms,
        calculateSelectedSlot,
        roomCapacities,
        handleUpdateRoom,
        setEccessoList,
    ]);

    const addRoom = useCallback(() => {
        if (!rooms || !Array.isArray(rooms)) {
            return;
        }
        const indexEccesso = eccessoList.findLastIndex(
            item => item === ENUM_ECCESSO.ADULT
        );
        if (eccessoList.length > 0 && indexEccesso > -1) {
            const left = eccessoList.slice(0, indexEccesso);
            const right = eccessoList.slice(indexEccesso + 1);
            eccessoList.splice(indexEccesso, 1);

            setEccessoList([...left, ...right]);
        }
        if (eccessoList.length > 0) {
            const isChildren =
                eccessoList.at(eccessoList.length - 1) ===
                ENUM_ECCESSO.CHILDREN;

            setEccessoList(eccessoList.slice(0, -1));
            dispatch({
                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                payload: [
                    ...rooms,
                    {
                        type: ERoomType.DOUBLE,
                        adults: rooms.length === 1 ? 0 : 1,
                        children: isChildren ? [1] : [],
                        newborns: 0,
                    },
                ],
            });
        } else if (rooms.length + 1 && calculateAdults === 1) {
            dispatch({
                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                payload: [
                    ...rooms,
                    {
                        type: ERoomType.DOUBLE,
                        adults: 1,
                        children: [],
                        newborns: 0,
                    },
                ],
            });
        } else {
            dispatch({
                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                payload: [
                    ...rooms,
                    {
                        type: ERoomType.DOUBLE,
                        adults: rooms.length === 1 ? 0 : 1,
                        children: [],
                        newborns: 0,
                    },
                ],
            });
        }

        dispatch({
            type: "SET_COUPON",
            payload: {
                code: "",
                value: 0,
            },
        });

        dispatch({
            type: "SET_TOTAL_AMOUNT",
            payload: originalAmount,
        });
    }, [
        dispatch,
        eccessoList,
        rooms,
        calculateAdults,
        originalAmount,
        setEccessoList,
    ]);

    const changeRoomTypeFromHighToLow = useCallback(
        (
            item: IRoom,
            index: number,
            type: ERoomType,
            newValue: number,
            oldValue: number
        ) => {
            if (eccessoList.length > 0) {
                const difference = newValue - oldValue;

                const extractValue = eccessoList.slice(
                    eccessoList.length - difference,
                    eccessoList.length
                );

                const lengthAdults = extractValue.filter(
                    el => el === ENUM_ECCESSO.ADULT
                ).length;

                const lengthChildrens = extractValue.filter(
                    el => el === ENUM_ECCESSO.CHILDREN
                ).length;

                const arrChildrens = Array.from(
                    { length: lengthChildrens },
                    () => 1
                );

                setEccessoList(
                    eccessoList.slice(0, eccessoList.length - difference)
                );

                const newRooms = rooms.map((room, idx) => {
                    if (index === idx) {
                        return {
                            type,
                            adults: room.adults + lengthAdults,
                            children: room.children.concat(arrChildrens),
                            newborns: room.newborns,
                        };
                    }
                    return room;
                });

                dispatch({
                    type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                    payload: newRooms,
                });
            } else {
                const newRooms = rooms.map((room, idx) => {
                    if (index === idx) {
                        return {
                            type,
                            adults: room.adults,
                            children: room.children,
                            newborns: room.newborns,
                        };
                    }
                    return room;
                });

                dispatch({
                    type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                    payload: newRooms,
                });
            }
        },
        [eccessoList, rooms, dispatch, setEccessoList]
    );

    const changeRoomType = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (item: IRoom, index: number, type: ERoomType) => {
            const valueRoom: { label: ERoomType; value: number }[] = [
                {
                    label: ERoomType.SINGLE,
                    value: 1,
                },
                {
                    label: ERoomType.DOUBLE,
                    value: 2,
                },
                {
                    label: ERoomType.TRIPLE,
                    value: 3,
                },
            ];

            const oldValue =
                valueRoom.filter(el => el.label === item.type).at(0)?.value ??
                2;

            const newValue =
                valueRoom.filter(el => el.label === type).at(0)?.value ?? 2;

            const roomUpdated: IRoom = {
                ...item,
                type,
            };

            if (oldValue < newValue) {
                changeRoomTypeFromHighToLow(
                    item,
                    index,
                    type,
                    newValue,
                    oldValue
                );
            } else {
                // eslint-disable-next-line no-lonely-if
                if (
                    item.adults + item.children.length <
                    (calculateSelectedSlot(roomUpdated)?.capacity ?? 0)
                ) {
                    if (eccessoList.length > 0) {
                        const difference = newValue - oldValue;
                        const extractValue = eccessoList.slice(
                            0,
                            eccessoList.length - difference
                        );

                        const lengthAdults = extractValue.filter(
                            el => el === ENUM_ECCESSO.ADULT
                        ).length;

                        const lengthChildrens = extractValue.filter(
                            el => el === ENUM_ECCESSO.CHILDREN
                        ).length;

                        const arrChildrens = Array.from(
                            { length: lengthChildrens },
                            () => 1
                        );

                        setEccessoList(
                            eccessoList.slice(
                                0,
                                eccessoList.length - difference
                            )
                        );

                        const newRooms = rooms.map((room, idx) => {
                            if (index === idx) {
                                return {
                                    type,
                                    adults: room.adults + lengthAdults,
                                    children:
                                        room.children.concat(arrChildrens),
                                    newborns: room.newborns,
                                };
                            }
                            return room;
                        });

                        dispatch({
                            type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                            payload: newRooms,
                        });
                    } else {
                        const newRooms = rooms.map((room, idx) => {
                            if (index === idx) {
                                return {
                                    type,
                                    adults: room.adults,
                                    children: room.children,
                                    newborns: room.newborns,
                                };
                            }
                            return room;
                        });

                        dispatch({
                            type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                            payload: newRooms,
                        });
                    }
                } else if (
                    item.adults + item.children.length ===
                    (calculateSelectedSlot(roomUpdated)?.capacity ?? 0)
                ) {
                    const newRooms = rooms.map((room, idx) => {
                        if (index === idx) {
                            return {
                                type,
                                adults: room.adults,
                                children: room.children,
                                newborns: room.newborns,
                            };
                        }
                        return room;
                    });

                    dispatch({
                        type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                        payload: newRooms,
                    });
                } else {
                    const difference = oldValue - newValue;

                    if (difference === 2) {
                        if (item.children.length === 2) {
                            setEccessoList(
                                eccessoList.concat([
                                    ENUM_ECCESSO.CHILDREN,
                                    ENUM_ECCESSO.CHILDREN,
                                ])
                            );

                            const newRooms = rooms.map((room, idx) => {
                                if (idx === index) {
                                    return {
                                        type,
                                        adults: item.adults,
                                        children: item.children.slice(0, -2),
                                        newborns: item.newborns,
                                    };
                                }
                                return room;
                            });

                            dispatch({
                                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                                payload: newRooms,
                            });
                        } else if (item.children.length === 1) {
                            if (item.adults > 1 && type === ERoomType.SINGLE) {
                                setEccessoList(
                                    eccessoList.concat([
                                        ENUM_ECCESSO.ADULT,
                                        ENUM_ECCESSO.CHILDREN,
                                    ])
                                );
                                const newRoomss = rooms.map((room, idx) => {
                                    if (idx === index) {
                                        return {
                                            type,
                                            adults: item.adults - 1,
                                            children: item.children.slice(
                                                0,
                                                -1
                                            ),
                                            newborns: item.newborns,
                                        };
                                    }
                                    return room;
                                });

                                dispatch({
                                    type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                                    payload: newRoomss,
                                });
                            } else {
                                setEccessoList(
                                    eccessoList.concat([ENUM_ECCESSO.CHILDREN])
                                );

                                const newRooms = rooms.map((room, idx) => {
                                    if (idx === index) {
                                        return {
                                            type,
                                            adults: item.adults,
                                            children: item.children.slice(
                                                0,
                                                -1
                                            ),
                                            newborns: item.newborns,
                                        };
                                    }
                                    return room;
                                });

                                dispatch({
                                    type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                                    payload: newRooms,
                                });
                            }
                        } else if (item.adults > 2) {
                            setEccessoList(
                                eccessoList.concat([
                                    ENUM_ECCESSO.ADULT,
                                    ENUM_ECCESSO.ADULT,
                                ])
                            );

                            const newRooms = rooms.map((room, idx) => {
                                if (idx === index) {
                                    return {
                                        type,
                                        adults: item.adults - 2,
                                        children: item.children,
                                        newborns: item.newborns,
                                    };
                                }
                                return room;
                            });

                            dispatch({
                                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                                payload: newRooms,
                            });
                        } else {
                            setEccessoList(
                                eccessoList.concat([ENUM_ECCESSO.ADULT])
                            );

                            const newRooms = rooms.map((room, idx) => {
                                if (idx === index) {
                                    return {
                                        type,
                                        adults: item.adults - 1,
                                        children: item.children,
                                        newborns: item.newborns,
                                    };
                                }
                                return room;
                            });

                            dispatch({
                                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                                payload: newRooms,
                            });
                        }
                    } else if (item.children.length > 0) {
                        setEccessoList(
                            eccessoList.concat([ENUM_ECCESSO.CHILDREN])
                        );

                        const newRooms = rooms.map((room, idx) => {
                            if (idx === index) {
                                return {
                                    type,
                                    adults: item.adults,
                                    children: item.children.slice(0, -1),
                                    newborns: item.newborns,
                                };
                            }
                            return room;
                        });

                        dispatch({
                            type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                            payload: newRooms,
                        });
                    } else {
                        setEccessoList(
                            eccessoList.concat([ENUM_ECCESSO.ADULT])
                        );

                        const newRooms = rooms.map((room, idx) => {
                            if (idx === index) {
                                return {
                                    type,
                                    adults: item.adults - 1,
                                    children: item.children,
                                    newborns: item.newborns,
                                };
                            }
                            return room;
                        });

                        dispatch({
                            type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                            payload: newRooms,
                        });
                    }
                }
            }

            dispatch({
                type: "SET_COUPON",
                payload: {
                    code: "",
                    value: 0,
                },
            });

            dispatch({
                type: "SET_TOTAL_AMOUNT",
                payload: originalAmount,
            });
        },
        [
            dispatch,
            eccessoList,
            rooms,
            calculateSelectedSlot,
            changeRoomTypeFromHighToLow,
            originalAmount,
            setEccessoList,
        ]
    );

    return (
        <>
            {/* <h5>{eccessoList}</h5> */}
            <h3 className={stylesCommon.configuratorTitle}>
                Add the numbers of rooms and travelers
            </h3>
            <div className={`${styles.containerTravellers} mb-4`}>
                <RowTravellerSectionDetail
                    textPopover="You need at least 1 room"
                    disableMinus={!rooms || rooms.length === 1}
                    showValue={rooms?.length ?? 0}
                    addMethod={() => {
                        // addNewRoom();
                        addRoom();
                    }}
                    minusMethod={() => {
                        if (rooms && rooms.length > 1) {
                            dispatch({
                                type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
                                payload: rooms.slice(0, -1),
                            });
                            dispatch({
                                type: "SET_COUPON",
                                payload: {
                                    code: "",
                                    value: 0,
                                },
                            });

                            dispatch({
                                type: "SET_TOTAL_AMOUNT",
                                payload: originalAmount,
                            });
                        }
                    }}
                    title="Rooms"
                />
                <RowTravellerSectionDetail
                    textPopover="You need at least 1 adult"
                    showValue={calculateAdults}
                    disableMinus={
                        !rooms || calculateAdults === (rooms.length ?? 0)
                    }
                    colorRow={eccessoList.includes(ENUM_ECCESSO.ADULT)}
                    addMethod={addNewAdult}
                    minusMethod={removeAdult}
                    title="Adults (12+)"
                />
                <RowTravellerSectionDetail
                    textPopover="The minimum is 0"
                    showValue={calculateChildrens}
                    disableMinus={
                        calculateChildrens === 0 &&
                        eccessoList.findIndex(
                            item => item === ENUM_ECCESSO.CHILDREN
                        ) === -1
                    }
                    disablePlus={eccessoList.includes(ENUM_ECCESSO.CHILDREN)}
                    textPopoverPlus="Max 2 children per room"
                    colorRow={eccessoList.includes(ENUM_ECCESSO.CHILDREN)}
                    addMethod={() => addNewChildren()}
                    minusMethod={() => {
                        // deleteChildren();
                        removeChildren();
                    }}
                    title="Children (4-12)"
                />
                <RowTravellerSectionDetail
                    textPopover="The minimum is 0"
                    showValue={calculatenewborns}
                    disableMinus={calculatenewborns === 0}
                    disablePlus={
                        !rooms || rooms.length * 3 === calculatenewborns
                    }
                    textPopoverPlus="Max 3 toddlers per room"
                    addMethod={() => addToddler()}
                    minusMethod={() => {
                        deletenewborns();
                    }}
                    title="Toddlers (0-3)"
                />
                <div className="mb-3 p-1 d-flex flex-row justify-content-between">
                    <div>
                        <p className={`${styles.containerTravellersText}`}>
                            Pets
                        </p>
                    </div>
                    <div className="position-sticky">
                        <MDBRow>
                            <MDBCol>
                                <MDBSwitch
                                    id="switchPets"
                                    checked={!!pets}
                                    className={
                                        pets
                                            ? styles.customSwitch
                                            : styles.customSwitchDisabled
                                    }
                                    onChange={value => {
                                        dispatch({
                                            type: "UPDATE_ORDER_DATA",
                                            payload: {
                                                pets: value.target.checked,
                                            },
                                        });
                                        if (setPets) {
                                            setPets(value.target.checked);
                                        }
                                        // const url = new URL(
                                        //     window.location.href
                                        // );
                                        // url.searchParams.set(
                                        //     "pets",
                                        //     value.target
                                        //         .checked as unknown as string
                                        // );
                                        // window.history.replaceState(
                                        //     null,
                                        //     "",
                                        //     url
                                        // );
                                    }}
                                />
                            </MDBCol>
                        </MDBRow>
                    </div>
                </div>
            </div>
            <div className={styles.containerTravellers}>
                <h3 className={stylesCommon.configuratorTitle}>Room types</h3>
                {(rooms || []).map((item, idx) => {
                    return (
                        <MDBRow className="mt-4">
                            <MDBCol size={5} center>
                                <p
                                    className={
                                        styles.containerTravellersRoomIndicator
                                    }
                                >
                                    Room {idx + 1}
                                </p>
                            </MDBCol>
                            <MDBCol size={7}>
                                <Input
                                    isSelectConfigurator
                                    value={
                                        item.type?.toLowerCase() ??
                                        ERoomType.DOUBLE
                                    }
                                    type="select"
                                    columnProps={{
                                        size: 12,
                                    }}
                                    onChangeSelect={value => {
                                        changeRoomType(
                                            item,
                                            idx,
                                            value.target.value as ERoomType
                                        );
                                    }}
                                    name="roomType"
                                    id="roomType"
                                    label=""
                                    required
                                    options={[
                                        ...roomTypeArrayOptions.map(lang => ({
                                            value: lang.value?.toLowerCase(),
                                            label: textCapitalize(lang.label),
                                        })),
                                    ]}
                                    inputClassName={styles.selectRoomType}
                                />
                            </MDBCol>
                        </MDBRow>
                    );
                })}
            </div>
            {errorGuest > 0 && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorContainerRow}>
                        <CloseLine />
                        <span className={styles.errorContainerRowText}>
                            {errorGuest > 1 ? "There are" : "There is"}{" "}
                            {errorGuest} {errorGuest > 1 ? "guests" : "guest"}{" "}
                            without a room, please add more.
                        </span>
                    </div>
                </div>
            )}
        </>
    );
};

export default RoomTravellersSectionDetail;
